import type { ElementComponent } from '../render/elementRenderContext';
import { choiceProp, numberProp, textProp } from '../render/elementProps';
import { FieldFrame, controlAttrs, useFieldBox } from './FieldFrame';
import type { FieldShape } from './formScope';
import { useFormField } from './useFormField';

/**
 * The two boxes a visitor types into. Both are controlled by the form around them, so what is typed
 * survives every other field's re-render and is still there when the form submits.
 *
 * What a merchant chose the input to accept is what it is checked against and nothing else: `email`
 * asks for an address, `tel` runs the same normaliser this storefront's checkout runs, and `text` asks
 * for nothing at all. The `type` also decides the keyboard a phone shows, which on a market that buys
 * by phone number is worth as much as the check.
 *
 * `defaultValue` is what the box holds before anyone types — a city a single-town shop always ships to,
 * a country code in front of a phone. It reaches the field as its *empty* value, not as its value, so
 * the shopper can still clear it: an answer they deleted stays deleted.
 */

/** What an input may accept, exactly as the catalog offers it to a merchant. */
const INPUT_TYPES = ['text', 'email', 'tel', 'number', 'password'] as const;

/** The rule each accepted type implies; a merchant chooses the type, never the rule behind it. */
const SHAPE_OF: Record<(typeof INPUT_TYPES)[number], FieldShape> = {
  text: 'none', email: 'email', tel: 'phone', number: 'number', password: 'none'
};

/** The browser's own name for what this field is for, where the type says it plainly. */
const FILLS: Partial<Record<(typeof INPUT_TYPES)[number], string>> = { email: 'email', tel: 'tel' };

// Draws one line a visitor types into, checked against whatever its merchant asked it to accept.
export const InputElement: ElementComponent = ({ node }) => {
  const kind = choiceProp(node.props, 'inputType', INPUT_TYPES, 'text');
  const field = useFormField(node, { shape: SHAPE_OF[kind], empty: textProp(node.props, 'defaultValue') });
  const box = useFieldBox(node);
  const placeholder = textProp(node.props, 'placeholder');
  return (
    <FieldFrame
      id={field.rule.domId} label={field.rule.label} required={field.rule.required}
      error={field.error} errorId={field.errorId} marks={box.marks}
    >
      <input
        {...controlAttrs(field, placeholder)}
        className={box.className}
        type={kind}
        dir="auto"
        autoComplete={FILLS[kind]}
        placeholder={placeholder || undefined}
        value={String(field.value)}
        onChange={(event) => field.onChange(event.target.value)}
      />
    </FieldFrame>
  );
};

// Draws several lines a visitor types into, as many lines tall as the merchant asked for.
export const TextareaElement: ElementComponent = ({ node }) => {
  const field = useFormField(node, { shape: 'none', empty: textProp(node.props, 'defaultValue') });
  const box = useFieldBox(node);
  const placeholder = textProp(node.props, 'placeholder');
  return (
    <FieldFrame
      id={field.rule.domId} label={field.rule.label} required={field.rule.required}
      error={field.error} errorId={field.errorId} marks={box.marks}
    >
      <textarea
        {...controlAttrs(field, placeholder)}
        className={box.className}
        dir="auto"
        rows={Math.min(Math.max(Math.trunc(numberProp(node.props, 'rows', 4)) || 4, 2), 20)}
        placeholder={placeholder || undefined}
        value={String(field.value)}
        onChange={(event) => field.onChange(event.target.value)}
      />
    </FieldFrame>
  );
};
